import React from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { AlertTriangle, Trash2, X } from 'lucide-react';

interface DeleteSourceConfirmationModalProps {
  sourceName: string;
  artifactCount: number;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteSourceConfirmationModal({
  sourceName,
  artifactCount,
  isOpen,
  onClose,
  onConfirm,
  isDeleting = false,
}: DeleteSourceConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Delete Source">
      <div className="space-y-4">
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-900 mb-1">Warning: This action cannot be undone</h3>
            <p className="text-sm text-red-700">
              Deleting this source will permanently remove all associated data from the system.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">What will be deleted:</h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Source:</strong> {sourceName}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Artifacts:</strong> {artifactCount} file{artifactCount !== 1 ? 's' : ''} uploaded from this source
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Chunks:</strong> All text chunks extracted from those artifacts
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Entities:</strong> All entities extracted from those artifacts
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Evidence:</strong> All evidence entries linked to those chunks
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 mt-1">•</span>
                <span>
                  <strong>Index Entries:</strong> All search index entries for those chunks
                </span>
              </li>
            </ul>
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              <strong>Note:</strong> Original files in storage are kept for audit purposes. Only database records are deleted.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            <X className="h-4 w-4" />
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            <Trash2 className="h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete Source'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

